import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Text, TextInput, UnstyledButton } from "@mantine/core"
import { useState } from "react"
import type { IProps } from "./interfaces"

export function EditableField({
  value,
  onSave,
  size = "sm",
  fw = 600,
  c = "var(--lex-text-brand)",
  component = "text",
  placeholder,
  underline = "solid",
  width,
}: IProps): React.JSX.Element {
  const { i18n } = useLingui()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const [saving, setSaving] = useState(false)

  const save = async (): Promise<void> => {
    if (!draft.trim() || draft.trim() === value) {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      await onSave(draft.trim())
      setEditing(false)
    } catch {
      setDraft(value)
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }

  if (editing) {
    return (
      <TextInput
        size={size}
        value={draft}
        onChange={(e) => setDraft(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") save()
          if (e.key === "Escape") {
            setDraft(value)
            setEditing(false)
          }
        }}
        onBlur={save}
        disabled={saving}
        autoFocus
        placeholder={placeholder}
        style={width !== undefined ? { display: "inline-block", width } : undefined}
        styles={component === "title" ? { input: { fontWeight: 700, fontSize: "1.4rem" } } : undefined}
      />
    )
  }

  return (
    <UnstyledButton
      onClick={() => {
        setDraft(value)
        setEditing(true)
      }}
    >
      <Text
        size={size}
        fw={fw}
        c={c}
        td={underline === "dotted" ? "underline dotted" : "underline"}
        style={{ cursor: "pointer" }}
        title={i18n._(msg`Click to edit`)}
      >
        {value}
      </Text>
    </UnstyledButton>
  )
}
